/**
 * taskDuration.ts
 * RISK-02 — single source of truth for the scenario task-table Duration column.
 *
 * Both Phase1TaskRow and Phase2TaskGrid render a "Duration (Days)" cell over the
 * task's booking span (min StartDate → max EndDate across its machine bookings).
 *
 * Semantics per run mode (Run Mode Phase C):
 *   attended (default) — WORKING days, not calendar days. Bookings skip weekends
 *     + stat holidays (working-day placement), so the column must too — otherwise
 *     a 2-day task that starts Friday and ends Monday reads as "4" (Fri/Sat/Sun/Mon)
 *     when only Fri + Mon are worked.
 *   continuous — CALENDAR days. The machine runs 24/7 through weekends, so the
 *     honest duration is the inclusive calendar span (a Sat end is real run-time).
 */

import { isContinuousRun, calendarDaysInclusive } from '../../../../utils/runModeRendering';

/**
 * Inclusive duration between two ISO ('YYYY-MM-DD') dates, per run mode.
 *
 * Attended (runMode omitted / 'attended') — WORKING days, Mon–Fri, excluding
 * any supplied stat-holiday dates:
 *   computeTaskDurationDays('2026-06-19', '2026-06-19')             → 1  (Fri)
 *   computeTaskDurationDays('2026-06-19', '2026-06-22')             → 2  (Fri + Mon; weekend carved out)
 *   computeTaskDurationDays('2026-06-19', '2026-06-22', ['2026-06-22']) → 1  (Mon is a holiday)
 *
 * Continuous ('continuous') — inclusive CALENDAR days (weekends count):
 *   computeTaskDurationDays('2026-08-04', '2026-08-25', [], 'continuous') → 22
 *
 * Returns null when either date is missing, the range is degenerate (end before
 * start), or an attended span contains no working days, so callers render '—'.
 */
export function computeTaskDurationDays(
  startDate: string | null,
  endDate: string | null,
  holidays: readonly string[] = [],
  runMode?: string | null,
): number | null {
  if (!startDate || !endDate || endDate < startDate) return null;
  if (isContinuousRun(runMode)) {
    const days = calendarDaysInclusive(startDate, endDate);
    return days > 0 ? days : null;
  }
  const holSet = new Set(holidays.map((h) => String(h).slice(0, 10)));
  let count = 0;
  let cur = startDate;
  // Bounded walk (guard caps absurd ranges) — inclusive of both endpoints.
  for (let guard = 0; cur <= endDate && guard < 4000; guard++) {
    const dow = new Date(`${cur}T00:00:00Z`).getUTCDay();
    if (dow !== 0 && dow !== 6 && !holSet.has(cur)) count++;
    const d = new Date(`${cur}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    cur = d.toISOString().slice(0, 10);
  }
  return count > 0 ? count : null;
}

/** What a Duration cell should render for a computed day count, per mode. */
export interface TaskDurationDisplay {
  /** Cell text: '9' (attended) or '22 cal days' (continuous). */
  text: string;
  /** Hover explanation of the unit. */
  tooltip: string;
}

/**
 * Per-mode Duration cell text + tooltip — the single source of truth for the
 * Phase-1 and Phase-2 grids (plan C5). Attended cells keep the bare number
 * (the column header already says "Days"); continuous cells say 'N cal days'
 * so 22 CALENDAR days is never mistaken for 22 working days.
 */
export function taskDurationDisplay(
  days: number | null,
  runMode?: string | null,
): TaskDurationDisplay {
  if (days == null) {
    return { text: '—', tooltip: 'No booked span yet.' };
  }
  if (isContinuousRun(runMode)) {
    return {
      text: `${days} cal day${days !== 1 ? 's' : ''}`,
      tooltip: `Continuous (24/7) test — ${days} CALENDAR day${days !== 1 ? 's' : ''} elapsed, weekends included.`,
    };
  }
  return {
    text: String(days),
    tooltip: `${days} working day${days !== 1 ? 's' : ''} (weekends + stat holidays skipped).`,
  };
}
